import fp from 'lodash/fp';
import queryString from 'query-string';
import React, { Component } from 'react';
import { Button, Col, Grid, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { Redirect } from 'react-router';
import { push } from 'react-router-redux';
import { branch, compose, lifecycle, renderComponent, renderNothing, withProps } from 'recompose';
import { bindActionCreators } from 'redux';
import { routes, userRoles } from '../../../constants';
import { validateEmail, validatePassword, isOrganizationPlan, getPlanFreeTrialDaysByPlanId } from '../../../services';
import { globalActions } from '../../../state/modules/global';
import { industryActions, industryRolesSelector } from '../../../state/modules/industry';
import { userActions } from '../../../state/modules/user';
import { Auth0Error, InputFormField, PasswordFormField } from '../../components';
import { getIndustryFromIdentifier, getNext, getOnboardingProcessFromPath, getRoleFromIndustry } from './industry';
/**
 * Responsible for sign up form and actions.
 */
class SignUp extends Component {

  constructor() {
    super();

    this.state = {
      signUpPayload: {
        pendingUser: {
          firstName: '',
          lastName: '',
          email: '',
          password: ''
        },
        pendingBusiness: {
          businessName: ''
        },
        industryRoleId: null,
        userRole: null
      },
      validation: {
        firstName: true,
        lastName: true,
        email: true,
        password: true,
        industryRole: true,
        businessName: true
      },
      isSignUpBusinessPlan: false
    };
  }

  chosenPlan() {
    return queryString.parse(this.props.location.search).chosenPlan;
  }

  componentDidMount() {
    this.init(this.props);
  }

  componentWillReceiveProps(nextProps) {
    this.init(nextProps);
  }

  init(nextProps) {
    const chosenPlanId = this.chosenPlan();
    if (!nextProps.payment.isFetching && nextProps.payment.plans.length > 0) {
      const availablePlanIds = fp.map((plan) => plan.id)(nextProps.payment.plans);

      // User must have nominated a plan to use before sign up, otherwise direct User to choose plan page.
      if (!chosenPlanId || !fp.includes(chosenPlanId)(availablePlanIds)) {
        this.props.goTo(routes.onboarding.choosePlan);
      }

      const chosenPlan = fp.find((plan) => plan.id === chosenPlanId)(nextProps.payment.plans);

      if (chosenPlan !== undefined) {
        this.setState({ isSignUpBusinessPlan: isOrganizationPlan(chosenPlan) });
      }
    }
  }

  onIndustryRoleChange(event) {
    let nextPayload = Object.assign({}, this.state.signUpPayload);
    nextPayload[event.target.name] = event.target.value;
    this.setState({ signUpPayload: nextPayload });
  }

  onPendingUserChange(event) {
    let nextPayload = Object.assign({}, this.state.signUpPayload);
    nextPayload.pendingUser[event.target.name] = event.target.value;
    this.setState({ signUpPayload: nextPayload });
  }

  onPendingBusinessChange(event) {
    let nextPayload = Object.assign({}, this.state.signUpPayload);
    nextPayload.pendingBusiness[event.target.name] = event.target.value;
    this.setState({ signUpPayload: nextPayload });
  }

  signUp(e) {
    e.preventDefault();
    e.stopPropagation();

    const {
      payment,
      defaultRole,
      signUp,
      nextUrl
    } = this.props

    if (this.validateForm()) {
      let pendingBusiness = null;
      if (this.state.isSignUpBusinessPlan) {
        pendingBusiness = {
          name: this.state.signUpPayload.pendingBusiness.businessName,
          email: this.state.signUpPayload.pendingUser.email,
          uploadCpdWithoutApproval: false,
        };
      }
      signUp(
        Object.assign({}, this.state.signUpPayload,
          { pendingBusiness: pendingBusiness, userRole: this.state.isSignUpBusinessPlan ? userRoles.BusinessAdmin : userRoles.User },
          { industryRoleId: defaultRole ? defaultRole.id : null }),
        fp.find((plan) => plan.id === this.chosenPlan())(payment.plans),
        nextUrl
      );
    }
  }

  /**
   * Validates the sign up form and sets the validation state.
   * 
   * Returns true if all fields are valid, false otherwise.
   */
  validateForm() {
    const validation = {
      firstName: this.state.signUpPayload.pendingUser.firstName.trim() !== '',
      lastName: this.state.signUpPayload.pendingUser.lastName.trim() !== '',
      email: validateEmail(this.state.signUpPayload.pendingUser.email),
      password: validatePassword(this.state.signUpPayload.pendingUser.password),
      businessName: !this.state.isSignUpBusinessPlan || this.state.signUpPayload.pendingBusiness.businessName.trim() !== ''
    };

    this.setState({ validation: validation });

    return fp.all(fp.identity)(validation);
  }

  render() {
    const freeTrialDays = getPlanFreeTrialDaysByPlanId(this.props.configuration.stripeConfig.plansInUse, this.chosenPlan());

    const firstNameProps = {
      id: 'first-name',
      validation: this.state.validation.firstName ? null : 'First name is required.',
      value: this.state.signUpPayload.pendingUser.firstName,
      required: true,
      onChange: (e) => this.onPendingUserChange(e),
      inputProps: {
        maxLength: 255
      }
    }

    const lastNameProps = {
      id: 'last-name',
      validation: this.state.validation.lastName ? null : 'Last name is required.',
      value: this.state.signUpPayload.pendingUser.lastName,
      required: true,
      onChange: (e) => this.onPendingUserChange(e),
      inputProps: {
        maxLength: 255
      }
    }

    const businessNameProps = {
      id: 'business-name',
      validation: this.state.validation.businessName ? null : 'Business name is required.',
      value: this.state.signUpPayload.pendingBusiness.businessName,
      required: true,
      onChange: (e) => this.onPendingBusinessChange(e),
      inputProps: {
        maxLength: 255
      }
    }

    const emailProps = {
      id: 'email',
      validation: this.state.validation.email ?
        null :
        this.state.signUpPayload.pendingUser.email ?
          'Email is required.' :
          'Must be a valid email address.',
      value: this.state.signUpPayload.pendingUser.email,
      required: true,
      onChange: (e) => this.onPendingUserChange(e),
      inputProps: {
        maxLength: 255
      }
    }

    const passwordProps = {
      id: 'password',
      validation: this.state.validation.password ? null : 'Password is required.',
      value: this.state.signUpPayload.pendingUser.password,
      required: true,
      onChange: (e) => this.onPendingUserChange(e)
    }

    return (
      <section id='content--signup'>
        <Grid>
          <h1 className="text-center">Welcome to Caddie.</h1>
          {
            this.props.user ?
              <Auth0Error user={this.props.user} /> :
              null
          }
          <Row className='no-gutter'>
            <Col xs={12} sm={6}>
              <form noValidate onSubmit={(e) => this.signUp(e)}>
                <h2 className='center-sm'>
                  {
                    this.state.isSignUpBusinessPlan ?
                      'First, let\'s get to know your business.' :
                      'First, let\'s get to know you.'
                  }
                </h2>
                <fieldset>
                  <InputFormField {...firstNameProps} />
                  <InputFormField {...lastNameProps} />
                  {
                    this.state.isSignUpBusinessPlan ?
                      <InputFormField {...businessNameProps} /> :
                      null
                  }
                  <InputFormField {...emailProps} />
                  <PasswordFormField {...passwordProps} />
                  <Button type='submit' className='large'>Continue</Button>
                </fieldset>
              </form>
            </Col>
            <Col xs={12} sm={5} smOffset={1}>
              <h2>Facts about the free trial</h2>
              <p>You won't be charged unless you choose to stick with us after your {freeTrialDays} day free trial. You can cancel at any time.</p>
              <p>This is a no-obligation free trial.</p>
              {
                this.state.isSignUpBusinessPlan ?
                  <p>Your subscription price will automatically increase as you add more members to you team.</p> :
                  null
              }
            </Col>
          </Row>
        </Grid>
      </section>
    );
  }
}

const mapStateToProps = (state, { signupIndustry }) => ({
  user: state.user,
  payment: state.payment,
  industry: state.industry,
  defaultRole: getRoleFromIndustry(industryRolesSelector(state), signupIndustry),
  configuration: state.configuration,
});

const mapDispatchToProps = (dispatch) => ({
  goTo: bindActionCreators(push, dispatch),
  selectPlan: bindActionCreators(globalActions.onboarding.selectPlan, dispatch),
  signUp: bindActionCreators(userActions.user.signUp, dispatch),
  getIndustries: bindActionCreators(industryActions.industries.get, dispatch),
});

export default compose(
  connect(
    (state) => ({
      industriesWithCpdCategorys: state.industry.industriesWithCpdCategorys
    })
  ),
  withProps(({
    match: {
      path,
      params: {
        industryIdentifier
      }
    },
    industriesWithCpdCategorys
  }) => ({
    signupIndustry: getIndustryFromIdentifier(industryIdentifier, industriesWithCpdCategorys),
  })),
  branch(
    ({ signupIndustry }) => signupIndustry === undefined,
    renderComponent(() => <Redirect to={routes.onboarding.invalid} />)
  ),
  withProps(({
    match: {
      path,
    },
    signupIndustry
  }) => ({
    nextUrl: getNext(signupIndustry, getOnboardingProcessFromPath(path))()
  })),
  connect(mapStateToProps, mapDispatchToProps),
  lifecycle({
    componentDidMount() {
      const { getIndustries, signupIndustry } = this.props
      getIndustries(signupIndustry)
    }
  }),
  branch(
    ({ defaultRole }) => defaultRole === undefined,
    renderNothing
  )
)(SignUp);



// WEBPACK FOOTER //
// ./src/containers/pages/onboarding/signup.js